import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// Auto-attach customer token to /auth/* and /bookings POST
api.interceptors.request.use((config) => {
  const userToken = localStorage.getItem("mopper_user_token");
  if (userToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${userToken}`;
  }
  return config;
});

export default api;

export function authHeaders() {
  const token = localStorage.getItem("mopper_admin_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function getPricing() {
  const { data } = await api.get("/pricing");
  return data;
}

export async function createBooking(payload) {
  const { data } = await api.post("/bookings", payload);
  return data;
}

export async function createQuote(payload) {
  const { data } = await api.post("/quotes", payload);
  return data;
}

export async function listMyQuotes() {
  const { data } = await api.get("/auth/quotes");
  return data;
}

export async function cancelMyQuote(id) {
  const { data } = await api.patch(`/auth/quotes/${id}/cancel`);
  return data;
}

export async function adminLogin(password) {
  const { data } = await api.post("/admin/login", { password });
  return data;
}

export async function listBookings() {
  const { data } = await api.get("/admin/bookings", { headers: { Authorization: `Bearer ${localStorage.getItem("mopper_admin_token") || ""}` } });
  return data;
}

export async function listMyBookings() {
  const { data } = await api.get("/auth/bookings");
  return data;
}

export async function cancelMyBooking(id) {
  const { data } = await api.patch(`/auth/bookings/${id}/cancel`);
  return data;
}

export async function updateBookingStatus(id, status) {
  const { data } = await api.patch(`/admin/bookings/${id}`, { status }, { headers: authHeaders() });
  return data;
}

export async function deleteBooking(id) {
  const { data } = await api.delete(`/admin/bookings/${id}`, { headers: authHeaders() });
  return data;
}

// ---- Admin Quotes ----
export async function listAdminQuotes() {
  const { data } = await api.get("/admin/quotes", { headers: authHeaders() });
  return data;
}

export async function getBillingRevenue() {
  const { data } = await api.get("/admin/billing-revenue", { headers: authHeaders() });
  return data;
}

export async function updateQuoteStatus(id, status) {
  const { data } = await api.patch(`/admin/quotes/${id}`, { status }, { headers: authHeaders() });
  return data;
}

export async function deleteAdminQuote(id) {
  const { data } = await api.delete(`/admin/quotes/${id}`, { headers: authHeaders() });
  return data;
}

export async function addQuoteNote(id, text) {
  const { data } = await api.post(`/admin/quotes/${id}/notes`, { text }, { headers: authHeaders() });
  return data;
}

export async function updateQuoteOps(id, ops) {
  const { data } = await api.patch(`/admin/quotes/${id}/ops`, ops, { headers: authHeaders() });
  return data;
}

// ---- Admin Teams ----
export async function listTeams() {
  const { data } = await api.get("/admin/teams", { headers: authHeaders() });
  return data;
}
export async function createTeam(payload) {
  const { data } = await api.post("/admin/teams", payload, { headers: authHeaders() });
  return data;
}
export async function updateTeam(id, payload) {
  const { data } = await api.patch(`/admin/teams/${id}`, payload, { headers: authHeaders() });
  return data;
}
export async function deleteTeam(id) {
  const { data } = await api.delete(`/admin/teams/${id}`, { headers: authHeaders() });
  return data;
}

// ---- Admin Vehicles ----
export async function listVehicles() {
  const { data } = await api.get("/admin/vehicles", { headers: authHeaders() });
  return data;
}
export async function createVehicle(payload) {
  const { data } = await api.post("/admin/vehicles", payload, { headers: authHeaders() });
  return data;
}
export async function updateVehicle(id, payload) {
  const { data } = await api.patch(`/admin/vehicles/${id}`, payload, { headers: authHeaders() });
  return data;
}
export async function deleteVehicle(id) {
  const { data } = await api.delete(`/admin/vehicles/${id}`, { headers: authHeaders() });
  return data;
}

// ---- Jobs (Admin) ----
export async function adminListJobs(status) {
  const { data } = await api.get(`/admin/jobs${status ? `?status=${status}` : ""}`, { headers: authHeaders() });
  return data;
}
export async function adminGetJob(id) {
  const { data } = await api.get(`/admin/jobs/${id}`, { headers: authHeaders() });
  return data;
}
export async function adminJobStats() {
  const { data } = await api.get("/admin/jobs/stats", { headers: authHeaders() });
  return data;
}
export async function adminCreateJob(payload) {
  const { data } = await api.post("/admin/jobs", payload, { headers: authHeaders() });
  return data;
}
export async function adminUpdateJob(id, payload) {
  const { data } = await api.put(`/admin/jobs/${id}`, payload, { headers: authHeaders() });
  return data;
}
export async function adminUpdateJobStatus(id, status) {
  const { data } = await api.patch(`/admin/jobs/${id}/status`, { status }, { headers: authHeaders() });
  return data;
}
export async function adminDuplicateJob(id) {
  const { data } = await api.post(`/admin/jobs/${id}/duplicate`, {}, { headers: authHeaders() });
  return data;
}
export async function adminDeleteJob(id) {
  const { data } = await api.delete(`/admin/jobs/${id}`, { headers: authHeaders() });
  return data;
}
export async function adminListJobApplications(jobId) {
  const { data } = await api.get(`/admin/jobs/${jobId}/applications`, { headers: authHeaders() });
  return data;
}
export async function adminListAllApplications() {
  const { data } = await api.get(`/admin/applications`, { headers: authHeaders() });
  return data;
}
export async function adminUpdateApplicationStatus(id, status, note) {
  const { data } = await api.patch(`/admin/applications/${id}/status`, { status, note }, { headers: authHeaders() });
  return data;
}
export async function adminDeleteApplication(id) {
  const { data } = await api.delete(`/admin/applications/${id}`, { headers: authHeaders() });
  return data;
}

// ---- Jobs (Public) ----
export async function publicListJobs() {
  const { data } = await api.get(`/jobs`);
  return data;
}
export async function publicGetJob(slug) {
  const { data } = await api.get(`/jobs/${slug}`);
  return data;
}
export async function publicApplyJob(slug, payload) {
  const { data } = await api.post(`/jobs/${slug}/apply`, payload);
  return data;
}
export async function publicGetCareersContent() {
  const { data } = await api.get(`/careers-content`);
  return data;
}
export async function publicSpontaneousApply(payload) {
  const { data } = await api.post(`/jobs/spontaneous-apply`, payload);
  return data;
}
export async function adminGetCareersContent() {
  const { data } = await api.get(`/admin/careers-content`, { headers: authHeaders() });
  return data;
}
export async function adminUpdateCareersContent(payload) {
  const { data } = await api.put(`/admin/careers-content`, payload, { headers: authHeaders() });
  return data;
}
// ---- Billing (Admin) ----
export async function adminListBilling(type, status, q) {
  const params = new URLSearchParams();
  if (type) params.append("type", type);
  if (status) params.append("status", status);
  if (q) params.append("q", q);
  const { data } = await api.get(`/admin/billing?${params.toString()}`, { headers: authHeaders() });
  return data;
}
export async function adminGetBilling(id) {
  const { data } = await api.get(`/admin/billing/${id}`, { headers: authHeaders() });
  return data;
}
export async function adminCreateBilling(payload) {
  const { data } = await api.post(`/admin/billing`, payload, { headers: authHeaders() });
  return data;
}
export async function adminUpdateBilling(id, payload) {
  const { data } = await api.put(`/admin/billing/${id}`, payload, { headers: authHeaders() });
  return data;
}
export async function adminBillingStatus(id, status, note) {
  const { data } = await api.patch(`/admin/billing/${id}/status`, { status, note }, { headers: authHeaders() });
  return data;
}
export async function adminBillingDuplicate(id) {
  const { data } = await api.post(`/admin/billing/${id}/duplicate`, {}, { headers: authHeaders() });
  return data;
}
export async function adminBillingConvert(id, opts = {}) {
  const url = opts.force ? `/admin/billing/${id}/convert-to-invoice?force=true` : `/admin/billing/${id}/convert-to-invoice`;
  const { data } = await api.post(url, {}, { headers: authHeaders() });
  return data;
}
export async function adminBillingCreateDeposit(quoteId, payload) {
  // payload: { percent?: number, amount?: number, description?: string }
  const { data } = await api.post(`/admin/billing/${quoteId}/create-deposit`, payload, { headers: authHeaders() });
  return data;
}
export async function adminBillingDelete(id) {
  const { data } = await api.delete(`/admin/billing/${id}`, { headers: authHeaders() });
  return data;
}
export function adminBillingPdfUrl(id) {
  const tok = localStorage.getItem("mopper_admin_token");
  return `${API}/admin/billing/${id}/pdf?token=${encodeURIComponent(tok || "")}`;
}
export function adminBillingPreviewUrl(id) {
  const tok = localStorage.getItem("mopper_admin_token");
  return `${API}/admin/billing/${id}/preview?token=${encodeURIComponent(tok || "")}`;
}
export async function adminBillingSend(id, payload) {
  const { data } = await api.post(`/admin/billing/${id}/send`, payload || {}, { headers: authHeaders() });
  return data;
}
export async function adminBillingQrStatus(id) {
  const { data } = await api.get(`/admin/billing/${id}/qr-status`, { headers: authHeaders() });
  return data;
}
export async function adminGetCompany() {
  const { data } = await api.get(`/admin/company-info`, { headers: authHeaders() });
  return data;
}
export async function adminSaveCompany(payload) {
  const { data } = await api.put(`/admin/company-info`, payload, { headers: authHeaders() });
  return data;
}
// ---------- Swiss QR-bill settings ----------
export async function adminGetQrSettings() {
  const { data } = await api.get(`/admin/settings/qr-bill`, { headers: authHeaders() });
  return data;
}
export async function adminSaveQrSettings(payload) {
  const { data } = await api.put(`/admin/settings/qr-bill`, payload, { headers: authHeaders() });
  return data;
}

// ---------- Clients module ----------
export async function adminListClients(params = {}) {
  const q = new URLSearchParams();
  if (params.q) q.set("q", params.q);
  if (params.type && params.type !== "all") q.set("type", params.type);
  if (params.archived) q.set("archived", "true");
  const { data } = await api.get(`/admin/clients?${q.toString()}`, { headers: authHeaders() });
  return data;
}
export async function adminGetClient(id) {
  const { data } = await api.get(`/admin/clients/${id}`, { headers: authHeaders() });
  return data;
}
export async function adminCreateClient(payload) {
  const { data } = await api.post(`/admin/clients`, payload, { headers: authHeaders() });
  return data;
}
export async function adminUpdateClient(id, payload) {
  const { data } = await api.put(`/admin/clients/${id}`, payload, { headers: authHeaders() });
  return data;
}
export async function adminArchiveClient(id, archived = true) {
  const { data } = await api.patch(`/admin/clients/${id}/archive?archived=${archived}`, {}, { headers: authHeaders() });
  return data;
}
export async function adminDeleteClient(id) {
  const { data } = await api.delete(`/admin/clients/${id}`, { headers: authHeaders() });
  return data;
}

// ---------- CRM extended endpoints ----------
export async function adminGetClientFull(id) {
  const { data } = await api.get(`/admin/clients/${id}/full`, { headers: authHeaders() });
  return data;
}
export async function adminCheckClientDuplicates(email, excludeId) {
  const q = new URLSearchParams({ email });
  if (excludeId) q.set("exclude_id", excludeId);
  const { data } = await api.get(`/admin/clients/duplicates?${q}`, { headers: authHeaders() });
  return data;
}
// Addresses
export async function adminListClientAddresses(clientId) {
  const { data } = await api.get(`/admin/clients/${clientId}/addresses`, { headers: authHeaders() });
  return data;
}
export async function adminCreateClientAddress(clientId, payload) {
  const { data } = await api.post(`/admin/clients/${clientId}/addresses`, payload, { headers: authHeaders() });
  return data;
}
export async function adminUpdateClientAddress(clientId, addrId, payload) {
  const { data } = await api.put(`/admin/clients/${clientId}/addresses/${addrId}`, payload, { headers: authHeaders() });
  return data;
}
export async function adminDeleteClientAddress(clientId, addrId) {
  const { data } = await api.delete(`/admin/clients/${clientId}/addresses/${addrId}`, { headers: authHeaders() });
  return data;
}
// Contacts
export async function adminCreateClientContact(clientId, payload) {
  const { data } = await api.post(`/admin/clients/${clientId}/contacts`, payload, { headers: authHeaders() });
  return data;
}
export async function adminUpdateClientContact(clientId, ctcId, payload) {
  const { data } = await api.put(`/admin/clients/${clientId}/contacts/${ctcId}`, payload, { headers: authHeaders() });
  return data;
}
export async function adminDeleteClientContact(clientId, ctcId) {
  const { data } = await api.delete(`/admin/clients/${clientId}/contacts/${ctcId}`, { headers: authHeaders() });
  return data;
}
// Notes
export async function adminCreateClientNote(clientId, payload) {
  const { data } = await api.post(`/admin/clients/${clientId}/notes`, payload, { headers: authHeaders() });
  return data;
}
export async function adminUpdateClientNote(clientId, noteId, payload) {
  const { data } = await api.put(`/admin/clients/${clientId}/notes/${noteId}`, payload, { headers: authHeaders() });
  return data;
}
export async function adminDeleteClientNote(clientId, noteId) {
  const { data } = await api.delete(`/admin/clients/${clientId}/notes/${noteId}`, { headers: authHeaders() });
  return data;
}
// Tasks
export async function adminCreateClientTask(clientId, payload) {
  const { data } = await api.post(`/admin/clients/${clientId}/tasks`, payload, { headers: authHeaders() });
  return data;
}
export async function adminUpdateClientTask(clientId, taskId, payload) {
  const { data } = await api.put(`/admin/clients/${clientId}/tasks/${taskId}`, payload, { headers: authHeaders() });
  return data;
}
export async function adminDeleteClientTask(clientId, taskId) {
  const { data } = await api.delete(`/admin/clients/${clientId}/tasks/${taskId}`, { headers: authHeaders() });
  return data;
}
// Activities
export async function adminCreateClientActivity(clientId, payload) {
  const { data } = await api.post(`/admin/clients/${clientId}/activities`, payload, { headers: authHeaders() });
  return data;
}

export async function adminAssignApplication(appId, jobId) {
  const { data } = await api.patch(`/admin/applications/${appId}/assign`, { jobId }, { headers: authHeaders() });
  return data;
}

// ============== Calendar / Interventions ==============
export async function adminListCalendarTeams() {
  const { data } = await api.get(`/admin/calendar-teams`, { headers: authHeaders() });
  return data;
}
export async function adminGetCalendarSettings() {
  const { data } = await api.get(`/admin/calendar-settings`, { headers: authHeaders() });
  return data;
}
export async function adminSaveCalendarSettings(payload) {
  const { data } = await api.put(`/admin/calendar-settings`, payload, { headers: authHeaders() });
  return data;
}
export async function adminListInterventions(params = {}) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") q.append(k, v); });
  const { data } = await api.get(`/admin/interventions?${q.toString()}`, { headers: authHeaders() });
  return data;
}
export async function adminGetIntervention(id) {
  const { data } = await api.get(`/admin/interventions/${id}`, { headers: authHeaders() });
  return data;
}
export async function adminCreateIntervention(payload) {
  const { data } = await api.post(`/admin/interventions`, payload, { headers: authHeaders() });
  return data;
}
export async function adminUpdateIntervention(id, patch) {
  const { data } = await api.patch(`/admin/interventions/${id}`, patch, { headers: authHeaders() });
  return data;
}
export async function adminDeleteIntervention(id) {
  const { data } = await api.delete(`/admin/interventions/${id}`, { headers: authHeaders() });
  return data;
}
export async function adminInterventionFromQuote(quoteId) {
  const { data } = await api.post(`/admin/interventions/from-quote/${quoteId}`, {}, { headers: authHeaders() });
  return data;
}
export async function adminCheckInterventionConflicts(payload) {
  const { data } = await api.post(`/admin/interventions/conflicts/check`, payload, { headers: authHeaders() });
  return data;
}
export function adminInterventionIcsUrl(id) {
  const t = encodeURIComponent(localStorage.getItem("mp_admin_token") || "");
  return `${api.defaults.baseURL}/admin/interventions/${id}.ics?token=${t}`;
}

// ============== Quote photos (public + admin) ==============
export async function uploadQuotePhoto(file, { onProgress } = {}) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post(`/quote-photos/upload`, form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => { if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100)); },
  });
  return data;
}
export function quotePhotoUrl(photoId) {
  return `${api.defaults.baseURL}/quote-photos/${photoId}`;
}
export async function adminListQuotePhotos(quoteId) {
  const { data } = await api.get(`/admin/quotes/${quoteId}/photos`, { headers: authHeaders() });
  return data;
}
export async function adminDeleteQuotePhoto(photoId) {
  const { data } = await api.delete(`/admin/quote-photos/${photoId}`, { headers: authHeaders() });
  return data;
}

// ============== Client files (documents libres attachés au dossier client) ==============
export async function adminListClientFiles(clientId) {
  const { data } = await api.get(`/admin/clients/${clientId}/files`, { headers: authHeaders() });
  return data;
}
export async function adminUploadClientFile(clientId, file, { label = "", onProgress } = {}) {
  const form = new FormData();
  form.append("file", file);
  if (label) form.append("label", label);
  const { data } = await api.post(`/admin/clients/${clientId}/files`, form, {
    headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => { if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100)); },
  });
  return data;
}
export async function adminDeleteClientFile(clientId, fileId) {
  const { data } = await api.delete(`/admin/clients/${clientId}/files/${fileId}`, { headers: authHeaders() });
  return data;
}
export function adminClientFileUrl(clientId, fileId) {
  const t = encodeURIComponent(localStorage.getItem("mopper_admin_token") || "");
  return `${API}/admin/clients/${clientId}/files/${fileId}?token=${t}`;
}
// Renvoie un blob URL téléchargeable (le token du localStorage ne peut pas être
// utilisé via `Authorization` sur un `<iframe>` ou `<img>` ; on récupère donc
// le contenu via axios puis on le sert en `blob:` local le temps de la session).
export async function adminFetchClientFileBlob(clientId, fileId) {
  const { data, headers } = await api.get(`/admin/clients/${clientId}/files/${fileId}`, {
    headers: authHeaders(), responseType: "blob",
  });
  return { blob: data, contentType: headers["content-type"] };
}

// ============== Prospection (SDR IA) ==============
const PB = "/admin/prospection";
export async function prospDashboard() {
  const { data } = await api.get(`${PB}/dashboard`, { headers: authHeaders() });
  return data;
}
export async function prospUploadImport(file, { onProgress } = {}) {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post(`${PB}/imports/upload`, form, {
    headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => { if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100)); },
  });
  return data;
}
export async function prospCommitImport(importId, autopilot = true) {
  const { data } = await api.post(`${PB}/imports/${importId}/commit?autopilot=${autopilot}`, {}, { headers: authHeaders() });
  return data;
}
export async function prospListImports() {
  const { data } = await api.get(`${PB}/imports`, { headers: authHeaders() });
  return data;
}
export async function prospContacts(params = {}) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) q.append(k, v); });
  const { data } = await api.get(`${PB}/contacts?${q.toString()}`, { headers: authHeaders() });
  return data;
}
export async function prospGetContact(id) {
  const { data } = await api.get(`${PB}/contacts/${id}`, { headers: authHeaders() });
  return data;
}
export async function prospUpdateContact(id, payload) {
  const { data } = await api.patch(`${PB}/contacts/${id}`, payload, { headers: authHeaders() });
  return data;
}
export async function prospAnalyzeContact(id, force = false) {
  const { data } = await api.post(`${PB}/contacts/${id}/analyze?force=${force}`, {}, { headers: authHeaders() });
  return data;
}
export async function prospAnalyzeBulk(ids, force = true) {
  const { data } = await api.post(`${PB}/contacts/analyze-bulk`, { ids, force }, { headers: authHeaders() });
  return data;
}
export async function prospSegments() {
  const { data } = await api.get(`${PB}/segments`, { headers: authHeaders() });
  return data;
}
export async function prospProcessPending(limit = 50) {
  const { data } = await api.post(`${PB}/process-pending?limit=${limit}`, {}, { headers: authHeaders() });
  return data;
}
export async function prospConvertContact(id) {
  const { data } = await api.post(`${PB}/contacts/${id}/convert`, {}, { headers: authHeaders() });
  return data;
}
export async function prospCompanies(q) {
  const { data } = await api.get(`${PB}/companies${q ? `?q=${encodeURIComponent(q)}` : ""}`, { headers: authHeaders() });
  return data;
}
export async function prospEmails(status) {
  const { data } = await api.get(`${PB}/emails${status ? `?status=${status}` : ""}`, { headers: authHeaders() });
  return data;
}
export async function prospUpdateEmail(id, payload) {
  const { data } = await api.patch(`${PB}/emails/${id}`, payload, { headers: authHeaders() });
  return data;
}
export async function prospCreateDraft(id) {
  const { data } = await api.post(`${PB}/emails/${id}/create-draft`, {}, { headers: authHeaders() });
  return data;
}
export async function prospSync() {
  const { data } = await api.post(`${PB}/sync`, {}, { headers: authHeaders() });
  return data;
}
export async function prospRelance(id) {
  const { data } = await api.post(`${PB}/emails/${id}/relance`, {}, { headers: authHeaders() });
  return data;
}
export async function prospGetSettings() {
  const { data } = await api.get(`${PB}/settings`, { headers: authHeaders() });
  return data;
}
export async function prospSaveSettings(payload) {
  const { data } = await api.put(`${PB}/settings`, payload, { headers: authHeaders() });
  return data;
}
export async function prospTestImap(imap) {
  const { data } = await api.post(`${PB}/settings/test-imap`, imap ? { imap } : {}, { headers: authHeaders() });
  return data;
}
export async function prospListKb() {
  const { data } = await api.get(`${PB}/kb`, { headers: authHeaders() });
  return data;
}
export async function prospUpdateKb(id, content) {
  const { data } = await api.put(`${PB}/kb/${id}`, { content }, { headers: authHeaders() });
  return data;
}
export async function prospSuppression() {
  const { data } = await api.get(`${PB}/suppression`, { headers: authHeaders() });
  return data;
}
export async function prospAddSuppression(email, reason) {
  const { data } = await api.post(`${PB}/suppression`, { email, reason }, { headers: authHeaders() });
  return data;
}
export async function prospDelSuppression(email) {
  const { data } = await api.delete(`${PB}/suppression/${encodeURIComponent(email)}`, { headers: authHeaders() });
  return data;
}

// ============== Live tracking (public + admin) ==============
export async function trackHeartbeat(session_id, page, referrer) {
  try { await api.post(`/track/heartbeat`, { session_id, page, referrer }); } catch { /* silencieux */ }
}
export async function trackEvent(session_id, type, meta) {
  try { await api.post(`/track/event`, { session_id, type, meta }); } catch { /* silencieux */ }
}
export async function liveNow() {
  const { data } = await api.get(`/admin/live/now`, { headers: authHeaders() });
  return data;
}
export async function liveStats(days = 14) {
  const { data } = await api.get(`/admin/live/stats?days=${days}`, { headers: authHeaders() });
  return data;
}


